import React, { useState, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { useSyncedStore } from '@syncedstore/react'
import { todoStore } from 'src/store/todo'
import { useParcelStore } from 'src/store/parcel'
import { checkForDuplicates } from 'src/utils/duplicates'
import Loader from 'src/components/shared/loader'
import Fallback from 'src/components/shared/fallback'
import Container from 'src/components/shared/container'
import PageHeader from 'src/components/shared/page-header'

type Zone = {
  id: string
  zone_moniker: string
  widget_zone_row?: number
  widget_zone_column?: number
}

type TaskType = {
  id: string
  task_type: string
  task_type_description: string
}

const GreenhouseGridPage: React.FC = () => {
  // navigation handled by PageHeader
  const { teamId } = useParams()
  const [searchParams] = useSearchParams()
  const teamName = searchParams.get('name') ?? ''
  const [selectedTasks, setSelectedTasks] = useState<string[]>([])
  const { parcel: parcelId } = useParcelStore()

  // Initialize all tasks as selected on first load
  const tasksInitialized = React.useRef(false)

  // Get data from Yjs
  const state = useSyncedStore(todoStore)

  const workers = state.workers?.filter(w => w.teamId === teamId && w.isActive) || []

  // Check for duplicates and warn
  React.useEffect(() => {
    checkForDuplicates(workers, 'Workers')
  }, [workers.length])
  const zones = state.zones?.filter(z => z.parcelId === parcelId && z.isActive) || []
  const tasks = useMemo(() =>
    (state.taskTypes || []).map(t => ({
      id: t.id,
      task_type: t.taskType,
      task_type_description: t.taskTypeDescription,
      task_type_moniker: t.taskTypeMoniker
    })), [state.taskTypes]
  )

  // Better color palette for task types - distinct and visible
  const taskColorPalette = ['#1B6EFF', '#39A64D', '#8667FF', '#00A5A7', '#FF9F1C']

  // Get task color based on task type ID
  const getTaskColor = (taskTypeId: string): string => {
    const taskIndex = tasks.findIndex((t) => t.id === taskTypeId)
    if (taskIndex === -1) return '#A3A3A8' // default gray
    return taskColorPalette[taskIndex % taskColorPalette.length]
  }

  // Initialize all tasks as selected on component mount
  React.useEffect(() => {
    if (!tasksInitialized.current && tasks.length > 0) {
      setSelectedTasks(tasks.map((t) => t.id))
      tasksInitialized.current = true
    }
  }, [tasks])

  // Group workers by zone from assignments - one entry per worker+task combination per zone
  // Note: Intentionally NOT memoized to ensure reactive updates from synced store
  const workersByZone: Record<string, Array<{ workerId: string; taskTypeId: string; locationCount: number }>> = {}

  state.workerAssignments.forEach((assignment) => {
    if (
      assignment.teamId === teamId &&
      !assignment.softDeleted &&
      (!selectedTasks.length || selectedTasks.includes(assignment.taskTypeId))
    ) {
      if (!workersByZone[assignment.zoneId]) {
        workersByZone[assignment.zoneId] = []
      }
      
      // Check if this worker+task combination already exists for this zone
      const existingEntry = workersByZone[assignment.zoneId].find(
        e => e.workerId === assignment.workerId && e.taskTypeId === assignment.taskTypeId
      )
      
      if (existingEntry) {
        // Increment location count
        existingEntry.locationCount += assignment.locationId ? 1 : 0
      } else {
        // New entry for this worker+task in this zone
        workersByZone[assignment.zoneId].push({
          workerId: assignment.workerId,
          taskTypeId: assignment.taskTypeId,
          locationCount: assignment.locationId ? 1 : 0,
        })
      }
    }
  })

  // Build worker map for quick lookup
  const workersMap = useMemo(() => {
    const map: Record<string, { id: string; first_name: string; last_name: string; email: string }> = {}
    workers.forEach((w) => {
      map[w.id] = {
        id: w.id,
        first_name: w.firstName,
        last_name: w.lastName,
        email: w.email
      }
    })
    return map
  }, [workers])

  // Build a proper grid: rows x columns with contiguous positions
  const buildGridLayout = useMemo(() => {
    
    // Find the dimensions
    let maxRow = 0
    let maxColumn = 0
    const zoneMap: Record<string, any> = {}

    zones.forEach((zone) => {
      const row = zone.widgetZoneRow ?? 0
      const col = zone.widgetZoneColumn ?? 0
      maxRow = Math.max(maxRow, row)
      maxColumn = Math.max(maxColumn, col)
      zoneMap[`${col}-${row}`] = {
        id: zone.id,
        zone_moniker: zone.zoneMoniker,
        widget_zone_row: zone.widgetZoneRow,
        widget_zone_column: zone.widgetZoneColumn
      }
    })

    // Create grid array: grid[column][row] = zone
    const fullGrid: Array<Array<Zone | null>> = []
    for (let col = 0; col <= maxColumn; col++) {
      fullGrid[col] = []
      for (let row = 0; row <= maxRow; row++) {
        fullGrid[col][row] = zoneMap[`${col}-${row}`] || null
      }
    }

    // Filter out completely empty columns
    const filteredGrid = fullGrid.filter(column => 
      column.some(zone => zone !== null)
    )

    // Find which rows have at least one zone
    const rowsWithZones = new Set<number>()
    zones.forEach((zone) => {
      const row = zone.widgetZoneRow ?? 0
      rowsWithZones.add(row)
    })
    const sortedRows = Array.from(rowsWithZones).sort((a, b) => a - b)

    return { grid: filteredGrid, rowIndices: sortedRows }
  }, [zones])

  const { grid, rowIndices } = buildGridLayout

  const toggleTask = (taskId: string) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((t) => t !== taskId)
        : [...prev, taskId]
    )
  }

  // Development testing: Remove all worker assignments (actually remove, not soft delete)
  const handleClearAllAssignments = () => {
    if (confirm('Are you sure you want to remove ALL worker assignments? This is for development testing.')) {
      if (!teamId) return

      // Remove all worker assignments for this team (actual removal, not soft delete)
      for (let i = state.workerAssignments.length - 1; i >= 0; i--) {
        const a = state.workerAssignments[i]
        if (a && a.teamId === teamId) {
          state.workerAssignments.splice(i, 1)
        }
      }

      // Remove all worker outs for this team
      if (Array.isArray(state.workerOuts)) {
        for (let i = state.workerOuts.length - 1; i >= 0; i--) {
          const wo = state.workerOuts[i]
          if (wo && wo.teamId === teamId) {
            state.workerOuts.splice(i, 1)
          }
        }
      }

      // Remove all assignment logs for this team
      if (Array.isArray(state.workerAssignmentLogs)) {
        for (let i = state.workerAssignmentLogs.length - 1; i >= 0; i--) {
          const log = state.workerAssignmentLogs[i]
          if (log && log.teamId === teamId) {
            state.workerAssignmentLogs.splice(i, 1)
          }
        }
      }
    }
  }


  if (zones.length === 0 || tasks.length === 0) return <Loader />
  if (zones.length === 0) return <Fallback title="No zones found" />

  return (
    <div className="min-h-screen w-full flex flex-col bg-gray-50">
      <PageHeader
        title={teamName}
        isDetail
        rightContent={
          <button
            onClick={handleClearAllAssignments}
            className="px-3 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex items-center gap-2"
            title="[Dev] Remove all assignments"
          >
            <Trash2 size={16} />
            Clear
          </button>
        }
      />

      <div className="flex-1 overflow-y-auto">
        <Container>
          <div className="flex flex-col gap-6 py-6 pb-32">
          {/* Task Filter - Horizontal Scrollable Buttons */}
          {tasks.length > 0 && (
            <div className="overflow-x-auto -mx-4 px-4">
              <div className="flex gap-3 flex-nowrap">
                {tasks.map((task: TaskType) => {
                  const taskColor = getTaskColor(task.id)
                  const isSelected = selectedTasks.includes(task.id)
                  
                  return (
                    <button
                      key={task.id}
                      onClick={() => toggleTask(task.id)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex-shrink-0 whitespace-nowrap ${
                        isSelected
                          ? 'text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      style={isSelected ? { backgroundColor: taskColor } : {}}
                    >
                      {task.task_type}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Zone Grid Table - All Columns from Data */}
          {grid.length > 0 ? (
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                <tbody>
                  {rowIndices.map((rowIdx) => (
                    <tr key={rowIdx} className="h-auto">
                      {grid.map((column, colIdx) => {
                        const zone = column[rowIdx]
                        const workersInZone = zone ? (workersByZone[zone.id] || []) : []

                        return (
                          <td
                            key={`${colIdx}-${rowIdx}`}
                            className="border border-gray-300 px-2 py-1 bg-white relative align-top min-h-[48px]"
                          >
                            {zone ? (
                              <>
                                {/* Zone Moniker - Background Text */}
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
                                  <span className="text-lg font-bold text-gray-400">{zone.zone_moniker}</span>
                                </div>

                                {/* Worker Initials Grid */}
                                <div className="relative py-0">
                                  <div
                                    className={`flex flex-wrap items-center gap-1 leading-none min-h-[28px] ${
                                      workersInZone.length === 0 ? 'justify-center text-gray-300 text-[10px]' : ''
                                    }`}
                                  >
                                    {workersInZone.length > 0 ? (
                                      workersInZone.map(({ workerId, taskTypeId, locationCount }) => {
                                        const worker = workersMap[workerId]
                                        if (!worker) return null

                                        const bgColor = getTaskColor(taskTypeId)
                                        const initials = `${worker.first_name?.[0]}${worker.last_name?.[0]}`.toUpperCase()
                                        const showLocationCount = locationCount > 1

                                        return (
                                          <div
                                            key={`${workerId}-${taskTypeId}-${zone.id}`}
                                            className="flex items-center gap-1"
                                          >
                                            <div
                                              className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold text-white shadow-sm leading-none"
                                              style={{ backgroundColor: bgColor }}
                                              title={`${worker.first_name} ${worker.last_name}`}
                                            >
                                              {initials}
                                            </div>
                                            {showLocationCount && (
                                                <span className="text-[9px] text-gray-600 font-medium leading-none inline-flex items-center">
                                                  ({locationCount})
                                                </span>
                                            )}
                                          </div>
                                        )
                                      })
                                    ) : (
                                      <span className="w-full text-center">
                                        —
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="h-full" />
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Fallback title="No zones configured for this greenhouse" />
          )}
          </div>
        </Container>
      </div>
    </div>
  )
}

export default GreenhouseGridPage
